import { ReactNode } from 'react';
import { OperationalStatusOptions } from '@components/EntityDrawers/constants/common';
import {
  EntityValidationStatus,
  Modes,
  OperationalStatus,
} from '@constants/canvas/layers';
import {
  NodeActionType,
  NodeDTO,
  NodeDTOTypes,
} from '@store/services/nodes/types';
import { CheckmarkCircleIcon, GlobalIcon, TimeIcon } from '@utils/iconsDefs';
import dayjs from 'dayjs';

export enum NodeActionNoChangeReason {
  NotEligibleType = 'not-eligible-type',
  EditingDisallowedInMode = 'editing-disallowed-in-mode',
  AlreadyHasStatus = 'already-has-status',
  TimelineConflict = 'timeline-conflict',
}

export type NodeActionEligibility =
  | { eligible: true }
  | { eligible: false; reason: NodeActionNoChangeReason };

const VALIDATION_NOT_ELIGIBLE_TYPES = [
  NodeDTOTypes.Characteristic,
  NodeDTOTypes.EvaluatedRisk,
  NodeDTOTypes.Initiatives,
];
const VALIDATION_DISALLOWED_IN_RISK_MANAGEMENT = [
  NodeDTOTypes.Capabilities,
  NodeDTOTypes.Functions,
];
const OPERATIONAL_STATUS_NODE_TYPES = [
  NodeDTOTypes.Service,
  NodeDTOTypes.Capabilities,
  NodeDTOTypes.Functions,
  NodeDTOTypes.Technologies,
];

function getValidationEligibility(
  node: Pick<NodeDTO, 'type'>,
  mode: Modes,
): NodeActionEligibility {
  if (VALIDATION_NOT_ELIGIBLE_TYPES.includes(node.type)) {
    return {
      eligible: false,
      reason: NodeActionNoChangeReason.NotEligibleType,
    };
  }
  if (
    mode === Modes.RiskManagement &&
    VALIDATION_DISALLOWED_IN_RISK_MANAGEMENT.includes(node.type)
  ) {
    return {
      eligible: false,
      reason: NodeActionNoChangeReason.EditingDisallowedInMode,
    };
  }
  return { eligible: true };
}

function getOperationalEligibility(
  node: Pick<NodeDTO, 'type'>,
  mode: Modes,
): NodeActionEligibility {
  if (!OPERATIONAL_STATUS_NODE_TYPES.includes(node.type)) {
    return {
      eligible: false,
      reason: NodeActionNoChangeReason.NotEligibleType,
    };
  }
  if (mode === Modes.RiskManagement) {
    return {
      eligible: false,
      reason: NodeActionNoChangeReason.EditingDisallowedInMode,
    };
  }
  if (mode === Modes.BusinessAlignment && node.type !== NodeDTOTypes.Service) {
    return {
      eligible: false,
      reason: NodeActionNoChangeReason.EditingDisallowedInMode,
    };
  }
  return { eligible: true };
}

function hasOperationalTimelineConflict(
  node: NodeActionChangeNode,
  value: string,
): boolean {
  const meta = (node.meta_data ?? {}) as { start_date?: string };
  const start = meta.start_date ? dayjs(meta.start_date) : null;
  const created = node.created_at ? dayjs(node.created_at) : null;
  if (!start?.isValid() || !created?.isValid()) return false;
  if (value === OperationalStatus.InDevelopment)
    return start.isBefore(created, 'day');
  return start.isAfter(created, 'day');
}

export type NodeActionChangeNode = Pick<
  NodeDTO,
  'id' | 'name' | 'type' | 'valid_status' | 'meta_data' | 'created_at'
>;

export type NodeActionChangePlan = {
  toChange: NodeActionChangeNode[];
  blocked: { node: NodeActionChangeNode; reason: NodeActionNoChangeReason }[];
  unchanged: { node: NodeActionChangeNode; reason: NodeActionNoChangeReason }[];
};

export interface NodeActionConfig {
  actionType: NodeActionType;
  label: string;
  menuLabel: string;
  menuText: string;
  menuToChangeTitle: string;
  menuToSkipTitle: string;
  menuConfirmName: string;
  successMessage: string;
  icon: ReactNode;
  getEligibility: (
    node: Pick<NodeDTO, 'type'>,
    mode: Modes,
  ) => NodeActionEligibility;
  statusOptions?: { value: string; label: string }[];
  keepMenuOpenOnApply?: boolean;
  immediateApplyWhenAllEligible?: boolean;
  readCurrent?: (node: NodeActionChangeNode) => string | undefined;
  hasConflict?: (node: NodeActionChangeNode, value: string) => boolean;
}

export const NODE_ACTIONS: Partial<Record<NodeActionType, NodeActionConfig>> = {
  [NodeActionType.ChangeValidationStatus]: {
    actionType: NodeActionType.ChangeValidationStatus,
    label: 'Set validation status',
    menuLabel: 'Set validation status?',
    menuText: 'The selected status cannot be applied to all selected entities.',
    menuToChangeTitle: 'Status will be set for',
    menuToSkipTitle: 'Status will not be set for',
    menuConfirmName: 'Set status',
    icon: <CheckmarkCircleIcon fill="currentColor" width={20} height={20} />,
    statusOptions: [
      { value: EntityValidationStatus.Draft, label: 'Draft' },
      { value: EntityValidationStatus.Validated, label: 'Validated' },
    ],
    successMessage: 'Validation status has been successfully updated.',
    keepMenuOpenOnApply: false,
    immediateApplyWhenAllEligible: true,
    readCurrent: (node) => node.valid_status,
    getEligibility: getValidationEligibility,
  },
  [NodeActionType.ChangeOperationalStatus]: {
    actionType: NodeActionType.ChangeOperationalStatus,
    label: 'Set operational status',
    menuLabel: 'Set operational status?',
    menuText: 'The selected status cannot be applied to all selected entities.',
    menuToChangeTitle: 'Status will be set for',
    menuToSkipTitle: 'Status will not be set for',
    menuConfirmName: 'Set status',
    icon: <TimeIcon fill="currentColor" width={20} height={20} />,
    statusOptions: OperationalStatusOptions,
    successMessage: 'Operational status has been successfully updated.',
    keepMenuOpenOnApply: true,
    immediateApplyWhenAllEligible: true,
    readCurrent: (node) =>
      (node.meta_data as { operational_status?: string } | undefined)
        ?.operational_status,
    getEligibility: getOperationalEligibility,
    hasConflict: hasOperationalTimelineConflict,
  },
  [NodeActionType.AddTargetScope]: {
    actionType: NodeActionType.AddTargetScope,
    label: 'Add target scope',
    menuLabel: 'Add target scope?',
    menuText: 'Target scope cannot be applied to all selected entities.',
    menuToChangeTitle: 'Target scope will be added for',
    menuToSkipTitle: 'Target scope will not be added for',
    menuConfirmName: 'Add target scope',
    icon: <GlobalIcon fill="currentColor" width={20} height={20} />,
    successMessage: 'Target scope has been successfully added.',
    getEligibility: getTargetScopeEligibility,
  },
};

export function getNodeActionChangePlan(
  nodes: NodeActionChangeNode[],
  mode: Modes,
  value: string,
  config: NodeActionConfig,
): NodeActionChangePlan {
  const plan: NodeActionChangePlan = {
    toChange: [],
    blocked: [],
    unchanged: [],
  };
  nodes.forEach((node) => {
    const eligibility = config.getEligibility(node, mode);
    if (!eligibility.eligible) {
      plan.unchanged.push({ node, reason: eligibility.reason });
      return;
    }
    if (config.readCurrent?.(node) === value) {
      plan.unchanged.push({
        node,
        reason: NodeActionNoChangeReason.AlreadyHasStatus,
      });
      return;
    }
    if (config.hasConflict?.(node, value)) {
      plan.blocked.push({
        node,
        reason: NodeActionNoChangeReason.TimelineConflict,
      });
      return;
    }
    plan.toChange.push(node);
  });
  return plan;
}

export function isNodeActionVisible(
  nodes: Pick<NodeDTO, 'type'>[],
  mode: Modes,
  config: NodeActionConfig,
): boolean {
  return nodes.some((node) => config.getEligibility(node, mode).eligible);
}

const TARGET_SCOPE_INELIGIBLE_TYPES = [
  NodeDTOTypes.Characteristic,
  NodeDTOTypes.EvaluatedRisk,
];
const TARGET_SCOPE_DISALLOWED_IN_RISK_MANAGEMENT = [
  NodeDTOTypes.Capabilities,
  NodeDTOTypes.Functions,
];

export function getTargetScopeEligibility(
  node: Pick<NodeDTO, 'type'>,
  mode: Modes,
): NodeActionEligibility {
  if (TARGET_SCOPE_INELIGIBLE_TYPES.includes(node.type)) {
    return {
      eligible: false,
      reason: NodeActionNoChangeReason.NotEligibleType,
    };
  }
  if (
    mode === Modes.RiskManagement &&
    TARGET_SCOPE_DISALLOWED_IN_RISK_MANAGEMENT.includes(node.type)
  ) {
    return {
      eligible: false,
      reason: NodeActionNoChangeReason.EditingDisallowedInMode,
    };
  }
  return { eligible: true };
}

export function isTargetScopeVisible(
  nodes: Pick<NodeDTO, 'type'>[],
  mode: Modes,
): boolean {
  return nodes.some((node) => getTargetScopeEligibility(node, mode).eligible);
}

export type TargetScopeAddPlan = { willAdd: NodeDTO[]; willNotAdd: NodeDTO[] };

export function getTargetScopeAddPlan(
  nodes: NodeDTO[],
  mode: Modes,
  selectedDomainIds: string[] = [],
): TargetScopeAddPlan {
  const selectedAssetIds = new Set(
    nodes.filter((n) => n.type === NodeDTOTypes.Asset).map((n) => n.id),
  );
  const plan: TargetScopeAddPlan = { willAdd: [], willNotAdd: [] };
  nodes.forEach((node) => {
    const { eligible } = getTargetScopeEligibility(node, mode);
    const inheritsFromSelectedAsset =
      TARGET_SCOPE_INELIGIBLE_TYPES.includes(node.type) &&
      !!node.parent_asset &&
      selectedAssetIds.has(node.parent_asset);
    if (!eligible && !inheritsFromSelectedAsset) {
      plan.willNotAdd.push(node);
      return;
    }
    const alreadyScoped =
      selectedDomainIds.length > 0 &&
      selectedDomainIds.every((domainId) =>
        (node.domainNodes ?? []).some(
          (domain) => domain.type === 'direct' && domain.domain_id === domainId,
        ),
      );
    if (alreadyScoped) {
      plan.willNotAdd.push(node);
      return;
    }
    plan.willAdd.push(node);
  });
  return plan;
}
